import React, { useState, useEffect } from 'react'
import { Row, Col, Container, Dropdown, Nav, Tab, OverlayTrigger, Tooltip, Button, Modal, ModalFooter } from 'react-bootstrap'
import { apiHandler } from '../../api/apihandler';
import { GAME_LIST, STATIC_GAME_SESSION_START, STATIC_GAME_SESSION_SUBMIT } from '../../api/constants'
import LoaderModal from '../loaderModal';
import Swal from 'sweetalert2';

// Redux Selector / Action
import { useDispatch, useSelector } from 'react-redux';
import NoDataFound from '../../components/noDataFound';
import LetterDrawingComp from '../../components/gameComp/letterDrawingComp';
import { TapNumberGame } from '../../components/gameComp/TapNumberGame';
import { TapLetterGame } from '../../components/gameComp/TapLetterGame';
import { TraceLetterGame } from '../../components/gameComp/TraceLetterGame';
import { MatchCaseGame } from '../../components/gameComp/MatchcaseGame';
import { DragDropWordGame } from '../../components/gameComp/DragDropWordGame';
import { CountMatchGame } from '../../components/gameComp/CountMatchGame';
import { SimpleAdditionGame } from '../../components/gameComp/SimpleAdditionGame';
import { MatchDragLetter } from '../../components/gameComp/MatchDragLetter';
import { ColorShapePopGame } from '../../components/gameComp/ColorShapePopGame';
import { FruitBasketSortGame } from '../../components/gameComp/FruitBasketSortGame';
import { TapAnimalSoundGame } from '../../components/gameComp/TapAnimalSoundGame';
import { TapFruitColorGame } from '../../components/gameComp/TapFruitColorGame';
import { TapShapeGame } from '../../components/gameComp/TapShapeGame';
import { ColorMixingGame } from '../../components/gameComp/ColorMixingGame';
import { RainbowColorGame } from '../../components/gameComp/RainbowColorGame';
import { MusicalInstrumentsGame } from '../../components/gameComp/MusicalInstrumentsGame';
import { ColorShadesGame } from '../../components/gameComp/ColorShadesGame';
import { SoundEffectsGame } from '../../components/gameComp/SoundEffectsGame';
import {
    BrainGameBoard,
    getBrainGameKey,
    isBrainBoostCategory,
    buildStaticBrainGames,
    saveStaticBrainProgress,
} from './LearningZoneGames';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';
import { AiAdaptiveGame } from '../../components/gameComp/AiAdaptiveGame';
import { GameFitBox } from './GameFitBox';

// Every id the modal below actually renders a game component for. Any
// game whose id isn't in this list (a gap like 3/4, anything past 22, or
// a new game added on the backend before its frontend case is wired up)
// used to open the modal to a silently blank board with nothing to play
// and no way out but the close button - see the fallback rendered after
// the id checks below.
const HANDLED_GAME_IDS = new Set([1, 2, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22]);


const levels = Array.from({ length: 20 }, (_, i) => ({
    id: i + 1,
    unlocked: i === 0, // Only first level unlocked initially
    stars: 0, // No stars initially
    // type: i < 12 ? 'letter' : 'number',
    game: (() => {
        if (i < 1) return 'tapLetter';
        if (i < 2) return 'tapLetterRandom';
        if (i < 3) return 'tapNumber';
        if (i < 4) return 'tapNumberRandom';
        if (i < 5) return 'countMatch';
        if (i < 6) return 'countMatchRandom';
        if (i < 7) return 'traceLetter';
        if (i < 8) return 'MatchDragLetter';
        if (i < 9) return 'matchCase';
        if (i < 10) return 'dragDropWord';
        if (i < 11) return 'ColorShapePopGame';
        if (i < 12) return 'TapFruitColorGame';
        if (i < 13) return 'FruitBasketSortGame';
        if (i < 14) return 'TapShapeGame';
        if (i < 15) return 'TapAnimalSoundGame';
        return 'simpleAddition';
    })()
}));


const GameLevelItems = ({ catData, isFirstCategory = false, categoryIndex = 0, userType, assignedGameNameIds = [], targetGameNameId = null, shouldAutoPlay = false, actingStudentId = null, activeTab = 'static' }) => {
    let profileData = (useSelector((state) => state.setting.setting.user_data))
    profileData = profileData ? JSON.parse(profileData) : "";
    
    const [gameListByCategory, setGameListByCategory] = useState([]);
    const [showLoader, setShowLoader] = useState(false);
    const [currentLevel, setCurrentLevel] = useState(1);
    const [showLetterDrawingModal, setShowLetterDrawingModal] = useState(false);
    const [currentGameData, setCurrentGameData] = useState(null);
    const [autoPlayHandled, setAutoPlayHandled] = useState(false);
    // Set when a static game's session actually started on the backend -
    // handleGameComplete only submits (and thus only writes game_sessions/
    // game_rounds/domain_mastery) when this is present, so a failed start
    // doesn't silently produce an orphaned submit.
    const [staticSessionId, setStaticSessionId] = useState(null);

    // Game names come from the backend inconsistently cased - live data
    // showed "trace Letter" and "matchCase" sitting next to properly-cased
    // "Tap Letter" and "Match Drag Letter" in the same grid. Normalize for
    // display only; the raw gameData.name is untouched everywhere else
    // (assignment matching, API payloads, etc.).
    const toDisplayTitle = (name) => {
        if (!name) return name;
        return name
            .replace(/([a-z])([A-Z])/g, '$1 $2') // "matchCase" -> "match Case"
            .split(/\s+/)
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Function to get game-specific icons
    // 'animal'/'sound' and 'fruit'/'color' were each sharing one icon via
    // `||`, so "Sound Effects Game" (no animal in it) showed paw prints ­-
    // it was piggybacking on the animal check - and "Color Mixing Game",
    // "Rainbow Color Game", "Color Shades Game" all showed a fruit apple
    // because 'color' was bundled into the fruit check. There was also no
    // rule at all for music, so "Musical Instruments Game" fell through to
    // the generic controller. Verified live: every game in the Sound and
    // Color categories was showing the wrong icon before this fix.
    const getGameIcon = (gameName, gameType) => {
        const name = (gameName || '').toLowerCase();
        const type = (gameType || '').toLowerCase();

        if (name.includes('letter') || type.includes('letter')) return '📝';
        if (name.includes('number') || type.includes('number') || name.includes('math')) return '🔢';
        if (name.includes('animal')) return '🐾';
        if (name.includes('music') || name.includes('instrument')) return '🎹';
        if (name.includes('sound')) return '🔊';
        if (name.includes('fruit')) return '🍎';
        if (name.includes('color')) return '🎨';
        if (name.includes('shape')) return '🔶';
        if (name.includes('match') || name.includes('memory')) return '🧩';
        if (name.includes('draw') || name.includes('trace')) return '✏️';
        if (name.includes('puzzle') || name.includes('jigsaw')) return '🧩';
        if (name.includes('coding') || name.includes('code')) return '🤖';
        if (name.includes('odd one')) return '🔍';
        if (name.includes('pattern')) return '🔁';
        return '🎮';
    };

    const assignedGameIdSet = new Set(
        (assignedGameNameIds || [])
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id))
    );

    const isPrivilegedUser = () => {
        // A teacher browsing Learning Zone on behalf of a student
        // (actingStudentId set - see the Attendance "Play Games" button)
        // gets every game unlocked, assigned or not - the teacher is
        // choosing what to open for that child directly, so the
        // assignment gate (which exists to stop a CHILD from wandering
        // into un-assigned games unsupervised) doesn't apply here. The
        // child's own login is untouched by this - actingStudentId is
        // never set there, so their lock/unlock still runs exactly as
        // before, gated on real assignment/completion.
        if (actingStudentId) {
            return true;
        }

        const isClassAvailable = process.env.REACT_APP_IS_CLASS_AVAILABLE === "1" || process.env.REACT_APP_IS_CLASS_AVAILABLE === 1;
        const isInfluencer = profileData?.user_setting?.is_influencer === 1 || profileData?.user_setting?.is_influencer === "1";
        const isDanceCorner = profileData?.user_setting?.is_dance_corner === 1 || profileData?.user_setting?.is_dance_corner === "1";

        if (isClassAvailable && (isInfluencer || isDanceCorner)) {
            return true;
        }

        return userType === 2 || userType === 3;
    };

    // AI-adaptive launcher tiles (is_ai_generated=1) are open-ended, self-
    // directed practice recommended by the mastery engine, not
    // teacher-assigned curriculum - gating them behind assignment would
    // make the whole adaptive-practice feature unreachable, since no
    // teacher workflow assigns these launcher rows. Always open for the
    // student they belong to.
    const isAiAdaptiveGame = (gameData) => Number(gameData?.is_ai_generated) === 1;

    // Frontend-only Brain Boost games (see buildStaticBrainGames): no backend
    // id, so no session/submit - progress is kept per child in localStorage.
    const isStaticBrainGame = (gameData) => !!gameData?.staticKey;
    const progressChildId = String(actingStudentId || getEffectiveStudentId(profileData) || '') || undefined;

    // Play Games tab = static games, Adaptive Games tab = AI-adaptive
    // launchers - a category is only rendered at all when it has at least
    // one game of the active tab's type (see game-category.js's
    // has_static_games/has_adaptive_games filtering), and within it only
    // the matching tiles are shown/interactive.
    const visibleGameList = gameListByCategory.filter((gameData) =>
        activeTab === 'adaptive' ? isAiAdaptiveGame(gameData) : !isAiAdaptiveGame(gameData)
    );

    // Non-privileged students can play only assigned or already completed games.
    const isLevelUnlocked = (gameData) => {
        if (!gameData) return false;

        if (isPrivilegedUser() || isAiAdaptiveGame(gameData) || isStaticBrainGame(gameData)) {
            return true;
        }

        const gameId = Number(gameData?.id);
        const isAssigned = assignedGameIdSet.has(gameId);
        const isCompleted = gameData?.is_completed === true || Number(gameData?.is_completed) === 1;

        return isAssigned || isCompleted;
    };

    // AI-adaptive tiles start their own session inside AiAdaptiveGame.js -
    // only static games are tracked here. Best-effort: a failed start
    // still opens the game (a network hiccup shouldn't block play) but
    // handleGameComplete will skip submitting progress since
    // staticSessionId stays null. Used both for a direct tile click and
    // for "Continue to Next Level" below - every game opening needs its
    // own fresh session, since a session can only be submitted once.
    const startStaticSessionForGame = async (gameData) => {
        setStaticSessionId(null);
        if (isAiAdaptiveGame(gameData) || isStaticBrainGame(gameData)) return;
        try {
            const res = await apiHandler(STATIC_GAME_SESSION_START, 'POST', {
                game_name_id: gameData.id,
                ...(actingStudentId ? { student_id: actingStudentId } : {})
            });
            if (res.data?.response?.status === 200) {
                setStaticSessionId(res?.data?.data?.session_id ?? null);
            }
        } catch (error) {
            console.error('Error starting static game session:', error);
        }
    };

    const handleLevelClick = async (gameData, index) => {
        console.log('Level clicked:', {
            gameData,
            index,
            isUnlocked: isLevelUnlocked(gameData),
            isFirstCategory,
            categoryIndex
        });

        if (isLevelUnlocked(gameData)) {
            await startStaticSessionForGame(gameData);
            setCurrentGameData(gameData);
            setCurrentLevel(index + 1); // Level starts from 1
            setShowLetterDrawingModal(true);
        } else {
            const title = '🔒 Game Locked';
            const message = 'Only teacher-assigned games can be played right now. Completed games will stay unlocked.';

            Swal.fire({
                title: title,
                text: message,
                icon: 'info',
                confirmButtonColor: '#4CAF50',
                confirmButtonText: 'OK',
                background: '#fff3e0',
                allowOutsideClick: true,
                allowEscapeKey: true,
                customClass: {
                    popup: 'animated pulse'
                }
            }).then(() => {
                Swal.close();
            });
        }
    };

    // Save game progress and unlock next level
    const handleGameComplete = async (gameData, stars = 3, score = 100) => {
        try {
            setShowLoader(true);

            // Find the current game level (index + 1 since levels start from 1)
            // within the active tab's own list, so "next level" advances to
            // the next game of the SAME type rather than skipping across tabs.
            const currentGameIndex = visibleGameList.findIndex(game => game.id === gameData.id);
            const currentLevel = currentGameIndex + 1;

            let saved = false;
            if (isStaticBrainGame(gameData)) {
                saveStaticBrainProgress(progressChildId, gameData.staticKey, stars);
                saved = true;
            } else {
                if (!staticSessionId) {
                    // Session never started (network hiccup, or this render
                    // somehow reached completion without a click through
                    // handleLevelClick) - nothing to submit progress against.
                    throw new Error('No active game session to submit progress to');
                }

                // These games don't track per-question detail internally (most
                // don't even count wrong attempts - see TapLetterGame), so the
                // one real signal available is the star rating every game
                // already reports out of 3. Translated into "N of 3 rounds
                // correct" - a faithful, uniform mapping across every static
                // game - so a real session_id/game_rounds/domain_mastery
                // history exists to feed the same recommendation engine
                // AI-adaptive sessions do, instead of a bare game_users row.
                const correctRounds = Math.max(0, Math.min(3, Math.round(stars)));
                const rounds = Array.from({ length: 3 }, (_, i) => ({
                    prompt_label: gameData?.name || null,
                    is_correct: i < correctRounds,
                }));

                const apiPayload = {
                    session_id: staticSessionId,
                    rounds,
                    ...(actingStudentId ? { student_id: actingStudentId } : {})
                };

                console.log('Calling STATIC_GAME_SESSION_SUBMIT API with payload:', apiPayload);

                const res = await apiHandler(STATIC_GAME_SESSION_SUBMIT, "POST", apiPayload);
                // A session can only be submitted once - clear it immediately so
                // nothing downstream can accidentally resubmit against it.
                setStaticSessionId(null);
                saved = res.data?.response?.status === 200;
            }

            if (saved) {
                // Update local state to mark current game as completed
                setGameListByCategory(prev =>
                    prev.map((item, index) => {
                        if (item.id === gameData.id) {
                            return { ...item, is_completed: true, stars: stars, score: score };
                        }
                        return item;
                    })
                );

                // Close the modal first
                setShowLetterDrawingModal(false);

                // Turn the "Please Wait..." loader off before showing the
                // congratulations dialog below - it was only cleared in the
                // `finally` block, which doesn't run until after this whole
                // try block finishes, including the `await Swal.fire(...)`
                // for the completion dialog. That left the loader spinning
                // on screen for the entire time the congratulations dialog
                // was up, which reads as an infinite loader that never
                // clears until the student notices and dismisses the
                // (easy to miss, stacked-behind-the-spinner) dialog under it.
                setShowLoader(false);

                // "Continue to Next Level" used to always render, but all it
                // ever did was close the dialog - clicking it never actually
                // opened the next game. Figure out up front whether there
                // really is a next level AND whether this user is currently
                // allowed to play it (privileged user, teacher-assigned, or
                // already completed - same rule isLevelUnlocked uses
                // elsewhere), so the button can both look right and work:
                // - next level exists and is playable -> "Continue to Next
                //   Level" actually opens it on confirm.
                // - next level exists but isn't unlocked yet (not assigned) ->
                //   don't offer a "Continue" that would go nowhere; just an
                //   acknowledgement button.
                // - no next level (last in category) -> "Great!" as before.
                const nextGameIndex = currentGameIndex + 1;
                const nextGameData = visibleGameList[nextGameIndex];
                const isLastLevel = !nextGameData;
                const isNextLevelPlayable = !!nextGameData && (
                    isPrivilegedUser() ||
                    isAiAdaptiveGame(nextGameData) ||
                    assignedGameIdSet.has(Number(nextGameData?.id)) ||
                    nextGameData?.is_completed === true ||
                    Number(nextGameData?.is_completed) === 1
                );

                const nextLevelText = isLastLevel
                    ? 'You completed all levels in this category!'
                    : isNextLevelPlayable
                        ? 'Next level unlocked!'
                        : 'Ask your teacher to assign the next level.';

                // Show completion animation/notification
                const result = await Swal.fire({
                    title: '🎉 Level Completed!',
                    html: `
                        <div style="text-align: center;">
                            <h4>Level ${currentLevel} - ${toDisplayTitle(gameData.name)}</h4>
                            <div style="font-size: 2rem; margin: 1rem 0;">
                                ${'⭐'.repeat(stars)}
                            </div>
                            <p style="font-size: 1.1rem; color: #4CAF50; font-weight: 600;">
                                ${nextLevelText}
                            </p>
                            <p style="color: #666;">Score: ${score} points</p>
                        </div>
                    `,
                    icon: 'success',
                    showConfirmButton: true,
                    confirmButtonText: isNextLevelPlayable ? 'Continue to Next Level' : 'Great!',
                    confirmButtonColor: '#4CAF50',
                    background: '#f0f8ff',
                    customClass: {
                        popup: 'animated bounceIn'
                    }
                });

                // Force re-render to show unlocked next level
                setGameListByCategory(prev => [...prev]);

                // Actually continue: open the next level's game modal.
                if (isNextLevelPlayable && result.isConfirmed) {
                    await startStaticSessionForGame(nextGameData);
                    setCurrentGameData(nextGameData);
                    setCurrentLevel(nextGameIndex + 1);
                    setShowLetterDrawingModal(true);
                }

            } else {
                throw new Error('Failed to save game progress');
            }
        } catch (error) {
            console.error('Error saving game progress:', error);
            setShowLetterDrawingModal(false);
            Swal.fire({
                title: '⚠️ Error',
                text: 'Unable to save your progress. Please try again.',
                icon: 'error',
                confirmButtonColor: '#FF5722'
            });
        } finally {
            setShowLoader(false);
        }
    };

    const handleClose = () => {
        setShowLetterDrawingModal(false);
    }

    // Only for games without a hard-wired id case below.
    const currentBrainGameKey = currentGameData?.staticKey
        || (currentGameData && !HANDLED_GAME_IDS.has(Number(currentGameData.id))
            ? getBrainGameKey(currentGameData.name)
            : null);


    useEffect(() => {
        getGameListApi()
    }, [catData]);

    // Auto-open assigned game when it's loaded
    useEffect(() => {
        if (!shouldAutoPlay || !targetGameNameId || gameListByCategory.length === 0 || autoPlayHandled) {
            return;
        }

        const targetGameIndex = gameListByCategory.findIndex((game) => Number(game?.id) === Number(targetGameNameId));
        if (targetGameIndex >= 0) {
            const targetGame = gameListByCategory[targetGameIndex];
            const timer = setTimeout(() => {
                handleLevelClick(targetGame, targetGameIndex);
                setAutoPlayHandled(true);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [shouldAutoPlay, targetGameNameId, gameListByCategory, autoPlayHandled]);

    // Brain Boost category only: add the frontend-only games after the API ones.
    function withStaticBrainGames(games) {
        if (!isBrainBoostCategory(catData?.name)) return games;
        const extras = buildStaticBrainGames(games, progressChildId)
            .map((g, i) => ({ ...g, level: games.length + i + 1 }));
        return [...games, ...extras];
    }

    async function getGameListApi() {
        setShowLoader(true)
        try {
            // limit was 10 with no pagination UI anywhere below it - any
            // category with more than 10 games would silently lose the
            // rest (levels 11+ just never render, no error, no "load
            // more"). The modal below already handles up to 22 distinct
            // game ids, so 10 was already too low for a fully-stocked
            // category. Raised well past the actual catalog size instead
            // of the more involved fix of building real pagination for
            // what's still lightweight metadata, not media.
            const res = await apiHandler(GAME_LIST, "POST", {
                page: 1,
                limit: 100,
                category_id: catData?.id || "",
                ...(actingStudentId ? { student_id: actingStudentId } : {})
            })
            if (res.data?.response?.status === 200) {
                const gameData = res?.data?.data?.data?.data || [];

                // Process game data to ensure proper lock/unlock state
                const processedGameData = gameData.map((game, index) => {
                    // Determine completion from game_users array
                    let is_completed = game?.is_completed === true || Number(game?.is_completed) === 1;
                    let stars = Number(game?.stars || 0);
                    let score = Number(game?.score || 0);
                    if (Array.isArray(game.game_users) && game.game_users.length > 0) {
                        // If any game_user has is_complete === 1, mark as completed
                        const completedUser = game.game_users.find(u => Number(u?.is_complete) === 1);
                        if (completedUser) {
                            is_completed = true;
                            score = Number(completedUser?.score || score);
                            stars = Number(completedUser?.stars || stars);
                        }
                    }
                    return {
                        ...game,
                        is_completed,
                        stars,
                        score,
                        level: index + 1 // Ensure level numbering starts from 1
                    };
                });
                setGameListByCategory(withStaticBrainGames(processedGameData));
            } else {
                setGameListByCategory(withStaticBrainGames([]));
            }
        } catch (error) {
            console.error(error);
            setGameListByCategory(withStaticBrainGames([]));
        } finally {
            setShowLoader(false)
        }
    }


    return (
        <>
            <LoaderModal showLoader={showLoader} />

            {/* Game Level Items Grid */}
            {/* gridTemplateColumns was a hardcoded `repeat(3, 1fr)` at every
                width — each tile is a fixed 350px tall, so on a 375px phone
                three ~1fr columns (minus the 2.5rem gaps) squeezed to
                roughly 100px wide, crushing the icon/title/stars inside and
                forcing the whole grid to overflow horizontally instead of
                reflowing, which is the "Letter Hunt" mobile complaint.
                auto-fit/minmax lets the browser drop to 1-2 columns on
                narrow screens on its own, no breakpoint list to maintain.
                The 220px floor was still too small for these fixed-height
                cards: at ~900px it fit 3 skinny ~260px columns, so the
                42px gap read as a big, ugly gap relative to each card and
                the whole row looked disconnected/broken. Raising the floor
                to 320px drops that same width to 2 wider columns, so the
                same gap reads as a clean divider instead.
                A flat `minmax(320px, 1fr)` broke phones though: this grid's
                own wrapper (.game-category-section's padding + border in
                game-category.js) can leave under 300px of inner width on a
                narrow phone, and a bare 320px minimum is a hard floor - the
                grid forces the column (and the card in it) to 320px anyway
                and overflows past its own container's edges instead of
                shrinking, which reads as the card's border misaligned with
                the box drawn around it. minmax(min(320px, 100%), 1fr) caps
                that floor at whatever width is actually available, so a
                single column still fills its container exactly on phones. */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
                // Split gap so the vertical gutter (rowGap) comfortably clears
                // the level-badge circle, which floats 18px above each card's
                // own top edge (see "Level Badge" below) - on mobile, where
                // the grid collapses to a single column, a plain 2.5rem gap
                // left only ~22px of clearance for that circle, and any
                // rounding/box-model slop on top of that made consecutive
                // cards visually run into each other's corners.
                rowGap: '4rem',
                columnGap: '2.5rem',
                padding: '2.5rem 0',
                width: '100%',
                position: 'relative',
                zIndex: 1,
                background: 'linear-gradient(135deg, #f8fffc 60%, #e0f7fa 100%)',
                borderRadius: '40px',
                boxShadow: '0 12px 48px 0 rgba(0, 200, 255, 0.10)',
                minHeight: 600,
                overflow: 'visible',
            }}>
                {/* Animated background for the grid (balloons, stars, confetti) */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                    overflow: 'hidden',
                }}>
                    {/* Balloons */}
                    {[...Array(6)].map((_, i) => (
                        <div key={i} style={{
                            position: 'absolute',
                            left: `${10 + i * 15}%`,
                            top: `${5 + (i % 2) * 40}%`,
                            width: 48,
                            height: 70,
                            background: `radial-gradient(circle at 60% 40%, #${['ffb347', 'ff6961', '77dd77', 'aec6cf', 'f49ac2', 'fdfd96'][i]}, #fff0 80%)`,
                            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                            opacity: 0.22,
                            animation: `balloonFloat${i % 2} 7s ${i * 0.7}s infinite ease-in-out`,
                            border: '2px solid #fff',
                            boxShadow: '0 4px 16px 0 rgba(0,0,0,0.08)',
                        }} />
                    ))}
                    {/* Confetti */}
                    {[...Array(18)].map((_, i) => (
                        <div key={i} style={{
                            position: 'absolute',
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            width: 12,
                            height: 12,
                            background: `#${['FFD700', 'FF69B4', '00BFFF', '32CD32', 'FFA500', 'FF6347'][i % 6]}`,
                            borderRadius: '50%',
                            opacity: 0.18 + (i % 3) * 0.13,
                            animation: `confettiDrop 4.5s ${i * 0.2}s infinite linear`,
                            border: '1.5px solid #fff',
                        }} />
                    ))}
                </div>
                {visibleGameList.map((gameData, index) => {
                    const isAssigned = assignedGameIdSet.has(Number(gameData?.id));
                    const isUnlocked = isLevelUnlocked(gameData);
                    const isCompleted = gameData?.is_completed;
                    const isNext = isAssigned && !isCompleted;
                    // Apply the same yellow-orange gradient background for all items
                    let itemBackground = 'linear-gradient(135deg, #FFD700 60%, #FFA500 100%)';
                    return (
                        <div
                            key={gameData.id || index}
                            onClick={() => handleLevelClick(gameData, index)}
                            style={{
                                background: itemBackground,
                                borderRadius: '44px',
                                padding: '2.1rem 1.2rem 2.2rem 1.2rem',
                                boxSizing: 'border-box',
                                textAlign: 'center',
                                cursor: isUnlocked ? 'pointer' : 'not-allowed',
                                opacity: isUnlocked ? 1 : 0.5,
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: isNext
                                    ? '0 24px 80px rgba(46, 125, 50, 0.30), 0 0 40px rgba(129, 199, 132, 0.30)'
                                    : isCompleted
                                        ? '0 24px 80px rgba(255, 152, 0, 0.22)'
                                        : '0 14px 40px rgba(0, 0, 0, 0.13)',
                                position: 'relative',
                                overflow: 'visible',
                                transform: isNext ? 'scale(1.11) rotate(-2deg)' : 'scale(1)',
                                animation: isNext ? 'bounceWiggle 1.2s infinite' : 'none',
                                fontFamily: 'Comic Sans MS, Comic Sans, Chalkboard, "Baloo 2", "Fredoka One", cursive',
                                // Was minHeight/maxHeight/height all pinned to 350 with
                                // overflow: 'visible' above - a game name long enough to
                                // wrap to 2 lines (e.g. "Drag Drop Word" on a narrow phone
                                // column) pushes the icon/title/stars/pill stack past
                                // 350px, and since overflow is visible (not hidden) that
                                // extra content doesn't get clipped, it spills out past
                                // the card's bottom edge into the row gap - and on mobile,
                                // where cards stack in a single column, into the top of
                                // the next card. Dropping maxHeight/height so the box can
                                // grow past 350px when it needs to keeps all content
                                // inside the card's own box, so it can't bleed into the
                                // next row no matter how long a game's name is.
                                minHeight: 350,
                                filter: isCompleted ? 'drop-shadow(0 0 18px #FFD70088)' : isNext ? 'drop-shadow(0 0 14px #81C78488)' : 'none',
                            }}
                            onMouseEnter={(e) => {
                                if (isUnlocked) {
                                    e.currentTarget.style.transform = 'translateY(-16px) scale(1.15)';
                                    e.currentTarget.style.boxShadow = isNext
                                        ? '0 36px 100px rgba(46, 125, 50, 0.40), 0 0 50px rgba(129, 199, 132, 0.40)'
                                        : isCompleted
                                            ? '0 36px 100px rgba(255, 152, 0, 0.32)'
                                            : '0 24px 80px rgba(76, 175, 80, 0.22)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (isUnlocked) {
                                    e.currentTarget.style.transform = isNext ? 'scale(1.11) rotate(-2deg)' : 'scale(1)';
                                    e.currentTarget.style.boxShadow = isNext
                                        ? '0 24px 80px rgba(46, 125, 50, 0.30), 0 0 40px rgba(129, 199, 132, 0.30)'
                                        : isCompleted
                                            ? '0 24px 80px rgba(255, 152, 0, 0.22)'
                                            : '0 14px 40px rgba(0, 0, 0, 0.13)';
                                }
                            }}
                        >
                            {/* Removed left top corner icon for completed levels */}
                            {isNext && (
                                <div style={{
                                    position: 'absolute',
                                    right: '-18px',
                                    bottom: '-18px',
                                    fontSize: '2.3rem',
                                    zIndex: 10,
                                    transform: 'rotate(12deg)',
                                    pointerEvents: 'none',
                                    filter: 'drop-shadow(0 2px 8px #81C78488)',
                                }}>
                                    🎈
                                </div>
                            )}
                            {/* Progress Chain Line was removed: it was always drawn
                                pointing right off the card's edge to represent
                                "connects to the next level," which only makes sense
                                when the next level actually IS the card to the right.
                                In this multi-column grid the next level is only ever
                                to the right for the left-hand card in each row; for
                                the right-hand card the next level is really the start
                                of the row below, so its connector pointed off into
                                blank margin with nothing there - confirmed live on
                                every right-column card in every category. Fixing that
                                properly needs the component to know its own current
                                column count (not available from CSS auto-fit without
                                a resize observer just for a decoration), so the
                                simplest correct fix is removing the decoration. */}

                            {/* Level Badge with Enhanced Design */}
                            <div style={{
                                position: 'absolute',
                                top: '-18px',
                                right: '-18px',
                                width: '60px',
                                height: '60px',
                                background: isCompleted
                                    ? 'radial-gradient(circle at 60% 40%, #FFD700, #FFA500 80%)'
                                    : isNext
                                        ? 'radial-gradient(circle at 60% 40%, #2E7D32, #1B5E20 80%)'
                                        : isUnlocked
                                            ? 'radial-gradient(circle at 60% 40%, #4CAF50, #388E3C 80%)'
                                            : 'radial-gradient(circle at 60% 40%, #9E9E9E, #757575 80%)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.5rem',
                                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
                                border: '4px solid white',
                                zIndex: 3,
                                fontFamily: 'Fredoka One, Baloo 2, Comic Sans MS, cursive',
                                letterSpacing: 1,
                                animation: isCompleted ? 'starTwinkle 1.5s ease-in-out infinite' : 'none',
                            }}>
                                {isCompleted ? '🏆' : !isUnlocked ? '🔒' : isNext ? '📌' : index + 1}
                            </div>

                            {/* Next Level Indicator */}
                            {isNext && (
                                <div style={{
                                    position: 'absolute',
                                    top: '14px',
                                    left: '14px',
                                    background: 'linear-gradient(135deg, #FFC107, #FF9800)',
                                    color: 'white',
                                    padding: '0.4rem 1.1rem',
                                    borderRadius: '18px',
                                    fontSize: '1rem',
                                    fontWeight: '700',
                                    boxShadow: '0 2px 8px rgba(255, 193, 7, 0.4)',
                                    animation: 'bounce 1s infinite',
                                    fontFamily: 'Fredoka One, Baloo 2, Comic Sans MS, cursive',
                                }}>
                                    🧑‍🏫 ASSIGNED
                                </div>
                            )}

                            {/* Game Icon with Enhanced Animation */}
                            <div style={{
                                // Bigger picture so young kids can recognise the game at a glance
                                fontSize: 'clamp(5rem, 14vw, 7rem)',
                                lineHeight: 1.1,
                                marginBottom: '1.1rem',
                                filter: isUnlocked ? 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.18))' : 'grayscale(100%) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                                transform: isNext ? 'scale(1.18) rotate(-6deg)' : 'scale(1)',
                                transition: 'all 0.3s ease',
                                animation: isNext ? 'wiggle 0.7s infinite' : 'none',
                            }}>
                                {gameData.icon || getGameIcon(gameData.name || gameData.type, gameData.game_type)}
                            </div>

                            {/* Game Title */}
                            <h6 style={{
                                margin: '0.9rem 0',
                                color: isCompleted ? '#E65100' : isNext ? '#1B5E20' : isUnlocked ? '#2E7D32' : '#757575',
                                fontWeight: '900',
                                fontSize: '1.25rem',
                                lineHeight: '1.3',
                                textShadow: isNext ? '0 1px 2px rgba(0, 0, 0, 0.1)' : '0 1px 2px #fff',
                                fontFamily: 'Baloo 2, Fredoka One, Comic Sans MS, cursive',
                                letterSpacing: 1,
                            }}>
                                {gameData.name ? toDisplayTitle(gameData.name) : `Level ${index + 1}`}
                            </h6>

                            {/* Progress Stars with Animation */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '0.4rem',
                                margin: '1rem 0',
                            }}>
                                {[1, 2, 3].map((star) => (
                                    <span
                                        key={star}
                                        style={{
                                            fontSize: '1.7rem',
                                            color: (gameData.stars || 0) >= star ? '#FFD700' : '#E0E0E0',
                                            filter: (gameData.stars || 0) >= star
                                                ? 'drop-shadow(0 2px 4px rgba(255, 215, 0, 0.6))'
                                                : 'none',
                                            animation: (gameData.stars || 0) >= star ? 'starTwinkle 1.5s ease-in-out infinite' : 'none',
                                            animationDelay: `${star * 0.2}s`,
                                        }}
                                    >
                                        ⭐
                                    </span>
                                ))}
                            </div>

                            {/* Enhanced Status Text */}
                            {/* isCompleted used to fill this pill with the exact same
                                gold->orange gradient as the card behind it, so on the
                                card's own background it rendered as a near-invisible
                                white-outlined shape (only the border/text stood out) -
                                the "broken" look reported on mobile. Give it a cream/
                                white fill with a gold border instead so it reads as a
                                distinct badge against the gold card. */}
                            <div style={{
                                fontSize: isCompleted ? '0.95rem' : '1.15rem',
                                fontWeight: '900',
                                color: isCompleted ? '#8a4b00' : 'white',
                                background: isCompleted
                                    ? 'linear-gradient(135deg, #fffbea 0%, #fff1c2 100%)'
                                    : isNext
                                        ? 'linear-gradient(135deg, #FF9800, #F57C00)'
                                        : !isUnlocked
                                            ? 'linear-gradient(135deg, #9E9E9E, #757575)'
                                            : 'linear-gradient(135deg, #2196F3, #1976D2)',
                                padding: isCompleted ? '0.45rem 1.1rem' : '0.7rem 1.3rem',
                                borderRadius: isCompleted ? '18px' : '22px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: isCompleted ? '0.4rem' : '0.5rem',
                                marginTop: '0.7rem',
                                boxShadow: isCompleted ? '0 4px 14px rgba(0, 0, 0, 0.18)' : '0 2px 8px rgba(0, 0, 0, 0.18)',
                                textShadow: isCompleted ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.18)',
                                fontFamily: 'Fredoka One, Baloo 2, Comic Sans MS, cursive',
                                letterSpacing: 1,
                                border: isCompleted ? '2px solid #FFA500' : 'none',
                                transition: 'all 0.3s',
                                // animation removed for completed button
                            }}>
                                {isCompleted ? (
                                    <>
                                        <span style={{fontSize:'1.2rem',marginRight:'0.15rem'}}>🏅</span>
                                        <span style={{fontWeight:900, fontSize:'0.95rem', letterSpacing:1}}>Completed!</span>
                                    </>
                                ) : isNext ? '🧑‍🏫 Assigned' : !isUnlocked ? '🔒 Locked' : '▶️ Play Now!'}
                            </div>

                            {/* Enhanced Play Button */}
                            {/* Removed right bottom play/completed icon */}

                            {/* Unlock Animation Overlay */}
                            {isNext && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(129, 199, 132, 0.13), rgba(46, 125, 50, 0.13))',
                                    borderRadius: '32px',
                                    animation: 'unlockShimmer 2s ease-in-out infinite',
                                    pointerEvents: 'none',
                                }} />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Empty State */}
            {visibleGameList.length === 0 && !showLoader && (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem 2rem',
                    color: '#757575'
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎮</div>
                    <h4 style={{ color: '#424242', marginBottom: '0.5rem' }}>No Games Available</h4>
                    <p style={{ color: '#757575' }}>Check back soon for exciting new games!</p>
                </div>
            )}

            {/* Game Modal */}
            <Modal
                scrollable={true}
                show={showLetterDrawingModal}
                onHide={handleClose}
                size="xl"
                style={{
                    marginTop: 50,
                    marginBottom: 0,
                    borderRadius: 36,
                    padding: 0,
                }}
                className="custom-modal"
            >
                <Modal.Header className="d-flex justify-content-between align-items-center" style={{ border: "none", background: "none", padding: "24px 36px 0 36px" }}>
                    <h4 className="modal-title" id="post-modalLabel" style={{ color: "#3e2723", fontWeight: 900, textShadow: "2px 2px 12px #fff, 0 0 12px #e0c68a", fontSize: 32, letterSpacing: 2 }}>
                        Level {currentLevel}
                    </h4>
                    <button type="button" className="btn btn-secondary lh-1" onClick={handleClose} style={{ background: "#ffe082", border: "none", color: "#3e2723", borderRadius: 16, fontWeight: 700, fontSize: 22, boxShadow: "0 2px 8px #e0c68a" }}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </Modal.Header>
                <Modal.Body style={{ overflow: "hidden", maxHeight: "75vh", width: "100%", padding: 0, display: "flex", justifyContent: "center" }}>
                    <div className={`modal-board-bg-kids ${currentBrainGameKey ? 'modal-board-brain' : ''}`}>
                        {/* Scales the board down to fit the screen so no game ever needs a scrollbar. */}
                        <GameFitBox key={currentGameData?.id}>
                        {/* Game content inside board - use API id logic */}
                        {/* AI-adaptive launcher tiles run their own session/round
                            loop against the student-games API (start/submit) and
                            manage their own progress persistence server-side, so
                            they're checked first and skip the numeric-id switch
                            and the generic onGameComplete->STATIC_GAME_SESSION_SUBMIT
                            path below entirely - reusing that path would write a
                            second, conflicting game_users row alongside the one
                            StudentGamesController.sessionSubmit already syncs. */}
                        {currentGameData && isAiAdaptiveGame(currentGameData) && (
                            <AiAdaptiveGame
                                gameData={currentGameData}
                                categoryId={catData?.id}
                                actingStudentId={actingStudentId}
                                handleClose={handleClose}
                                onSessionEnd={() => {
                                    handleClose();
                                    getGameListApi();
                                }}
                            />
                        )}
                        {currentGameData?.id === 1 && <TapLetterGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} type="sequence" className="game-content" />}
                        {currentGameData?.id === 2 && <TapLetterGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} type="random" className="game-content" />}
                        {currentGameData?.id === 9 && <TraceLetterGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content"  />}
                        {currentGameData?.id === 10 && <MatchDragLetter onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} type="random" className="game-content"  />}
                        {currentGameData?.id === 11 && <MatchCaseGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} type="random" className="game-content" />}
                        {currentGameData?.id === 12 && <DragDropWordGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content"  />}
                        {currentGameData?.id === 5 && <TapNumberGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} type="sequence" className="game-content"  />}
                        {currentGameData?.id === 6 && <TapNumberGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} type="random" className="game-content"  />}
                        {currentGameData?.id === 7 && <CountMatchGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} type="sequence" className="game-content"  />}
                        {currentGameData?.id === 8 && <CountMatchGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} type="random" className="game-content"  />}
                        {currentGameData?.id === 15 && <FruitBasketSortGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content" />}
                        {currentGameData?.id === 13 && <ColorShapePopGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content"  />}
                        {currentGameData?.id === 16 && <TapShapeGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content"  />}
                        {currentGameData?.id === 14 && <TapFruitColorGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content"  />}
                        {currentGameData?.id === 17 && <TapAnimalSoundGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content" />}
                        {currentGameData?.id === 18 && <ColorMixingGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content" />}
                        {currentGameData?.id === 19 && <RainbowColorGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content" />}
                        {currentGameData?.id === 20 && <ColorShadesGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content" />}
                        {currentGameData?.id === 21 && <MusicalInstrumentsGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content" />}
                        {currentGameData?.id === 22 && <SoundEffectsGame onGameComplete={(stars, score) => {
                            setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                        }} handleClose={handleClose} className="game-content" />}
                        {/* <LetterDrawingComp/> */}
                        {/* Brain Boost games (Jigsaw, Color & Mix, Block
                            Coding, Odd One Out, Fix the Pattern) come from
                            the API with backend-assigned ids, so they're
                            matched by name - same completion / GAME_SAVE
                            flow as every game above. */}
                        {currentBrainGameKey && (
                            <BrainGameBoard
                                key={currentGameData?.id}
                                gameKey={currentBrainGameKey}
                                onGameComplete={(stars, score) => {
                                    setTimeout(() => handleGameComplete(currentGameData, stars, score), 500);
                                }}
                            />
                        )}
                        {currentGameData && !HANDLED_GAME_IDS.has(Number(currentGameData.id)) && !currentBrainGameKey && 
                        currentGameData && !isAiAdaptiveGame(currentGameData) && !HANDLED_GAME_IDS.has(Number(currentGameData.id)) && (
                            <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚧</div>
                                <h5 style={{ color: '#3e2723', fontWeight: 800 }}>This game isn't ready yet</h5>
                                <p style={{ color: '#6b4f3f' }}>Please close this and try another level - we're still building this one!</p>
                            </div>
                        )}
                        </GameFitBox>
                    </div>
                </Modal.Body>
            </Modal>
            <style>
                {`
                                        .custom-modal .modal-dialog {
                                            display: flex;
                                            align-items: stretch !important;
                                            justify-content: center;
                                            margin: 32px auto 32px auto;
                                            width: auto;
                                            max-width: 100vw;
                                            min-height: 0;
                                        }
                                        .custom-modal .modal-content {
                                            background-image: url('/image/back4.png');
                                            background-size: 100% 100%;
                                            background-position: center;
                                            color: #222;
                                        }
                                        /* Boards are scaled to fit (GameFitBox), so the game
                                           popup never shows a scrollbar. */
                                        .custom-modal,
                                        .custom-modal .modal-body,
                                        .custom-modal .modal-board-bg-kids * {
                                            scrollbar-width: none;
                                        }
                                        .custom-modal::-webkit-scrollbar,
                                        .custom-modal .modal-body::-webkit-scrollbar,
                                        .custom-modal .modal-board-bg-kids *::-webkit-scrollbar {
                                            display: none;
                                        }
                                        .modal-board-bg-kids {
                                            position: relative;
                                            z-index: 2;
                                            width: 100%;
                                            padding-bottom: 16px;
                                            animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
                                        }
                                        @keyframes board-pop {
                                            0% { transform: scale(0.9); opacity: 0; }
                                            100% { transform: scale(1); opacity: 1; }
                                        }
                                        /* Brain Boost games size to their container (unlike the
                                           other games, which bring their own fixed-width board),
                                           and this board otherwise shrink-wraps inside the
                                           centered flex body - collapsing them to a narrow
                                           column. Give it the full width, top-aligned so tall
                                           games scroll instead of being squeezed. */
                                        .modal-board-bg-kids.modal-board-brain {
                                            width: 100%;
                                            display: flex;
                                            justify-content: center;
                                            align-self: flex-start;
                                            flex-shrink: 0;
                                            padding: 0 16px 16px;
                                            box-sizing: border-box;
                                        }
                                        .modal-board-bg-kids h4,
                                        .modal-board-bg-kids .game-content {
                                            color: #3e2723;
                                            text-shadow: 2px 2px 12px #fff, 0 0 12px #e0c68a;
                                            font-weight: 900;
                                        }
                                        /* Desktop (default) */
                                        @media (max-width: 1200px) {
                                            .custom-modal .modal-content {
                                                max-width: 98vw;
                                            }
                                            .modal-board-bg-kids {
                                                max-width: 98vw;
                                            }
                                        }
                                        /* Tablet */
                                        @media (max-width: 900px) {
                                            .custom-modal .modal-dialog {
                                                margin: 16px auto 16px auto;
                                            }
                                            .custom-modal .modal-content {
                                                border-radius: 24px;
                                                max-height: calc(100vh - 32px);
                                            }
                                        }
                                        /* Mobile */
                                        @media (max-width: 700px) {
                                            .custom-modal .modal-dialog {
                                                margin: 8px auto 8px auto;
                                                min-height: 100vh;
                                            }
                                            .custom-modal .modal-content {
                                                min-height: 120px;
                                                max-height: calc(100vh - 16px);
                                                border-radius: 14px;
                                            }
                                        }
                                        @media (max-width: 480px) {
                                            .custom-modal .modal-content {
                                                border-width: 4px;
                                            }
                                            .modal-board-bg-kids {
                                                padding: 4px 0vw;
                                            }
                                        }
                                        /* Balloons animation */
                                        @keyframes balloonFloat0 {
                                                0% { transform: translateY(0) scale(1); }
                                                50% { transform: translateY(-30px) scale(1.05); }
                                                100% { transform: translateY(0) scale(1); }
                                        }
                                        @keyframes balloonFloat1 {
                                                0% { transform: translateY(0) scale(1); }
                                                50% { transform: translateY(-20px) scale(0.97); }
                                                100% { transform: translateY(0) scale(1); }
                                        }
                                        /* Confetti animation */
                                        @keyframes confettiDrop {
                                                0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
                                                100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
                                        }
                                `}
            </style>
            <style>{`
                                @keyframes nextLevelPulse {
                                        0%, 100% { box-shadow: 0 8px 30px rgba(46, 125, 50, 0.3), 0 0 20px rgba(129, 199, 132, 0.4); }
                                        50% { box-shadow: 0 12px 40px rgba(46, 125, 50, 0.6), 0 0 30px rgba(129, 199, 132, 0.8); }
                                }
                                @keyframes progressFlow {
                                        0% { opacity: 0.6; transform: translateX(-10px); }
                                        50% { opacity: 1; transform: translateX(0); }
                                        100% { opacity: 0.6; transform: translateX(10px); }
                                }
                                @keyframes bounce {
                                        0%, 100% { transform: translateY(0); }
                                        50% { transform: translateY(-8px); }
                                }
                                @keyframes bounceWiggle {
                                        0%, 100% { transform: scale(1.07) rotate(-2deg); }
                                        20% { transform: scale(1.13) rotate(2deg); }
                                        40% { transform: scale(1.09) rotate(-4deg); }
                                        60% { transform: scale(1.13) rotate(2deg); }
                                        80% { transform: scale(1.07) rotate(-2deg); }
                                }
                                @keyframes wiggle {
                                        0%, 100% { transform: scale(1.18) rotate(-6deg); }
                                        20% { transform: scale(1.22) rotate(6deg); }
                                        40% { transform: scale(1.18) rotate(-6deg); }
                                        60% { transform: scale(1.22) rotate(6deg); }
                                        80% { transform: scale(1.18) rotate(-6deg); }
                                }
                                @keyframes starTwinkle {
                                        0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
                                        25% { transform: scale(1.2) rotate(5deg); opacity: 0.8; }
                                        75% { transform: scale(0.9) rotate(-5deg); opacity: 0.9; }
                                }
                                @keyframes playButtonPulse {
                                        0%, 100% { transform: scale(1); }
                                        50% { transform: scale(1.1); }
                                }
                                @keyframes unlockShimmer {
                                        0% { opacity: 0; transform: translateX(-100%); }
                                        50% { opacity: 0.3; transform: translateX(0); }
                                        100% { opacity: 0; transform: translateX(100%); }
                                }
                        `}</style>

            {/* Game Level Animation Styles */}
            <style>{`
                @keyframes nextLevelPulse {
                    0%, 100% { box-shadow: 0 8px 30px rgba(46, 125, 50, 0.3), 0 0 20px rgba(129, 199, 132, 0.4); }
                    50% { box-shadow: 0 12px 40px rgba(46, 125, 50, 0.6), 0 0 30px rgba(129, 199, 132, 0.8); }
                }

                @keyframes progressFlow {
                    0% { opacity: 0.6; transform: translateX(-10px); }
                    50% { opacity: 1; transform: translateX(0); }
                    100% { opacity: 0.6; transform: translateX(10px); }
                }

                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-8px); }
                }

                @keyframes starTwinkle {
                    0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
                    25% { transform: scale(1.2) rotate(5deg); opacity: 0.8; }
                    75% { transform: scale(0.9) rotate(-5deg); opacity: 0.9; }
                }

                @keyframes playButtonPulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                }

                @keyframes unlockShimmer {
                    0% { opacity: 0; transform: translateX(-100%); }
                    50% { opacity: 0.3; transform: translateX(0); }
                    100% { opacity: 0; transform: translateX(100%); }
                }
            `}</style>
        </>
    )

}

export default GameLevelItems